/**
 * @module M/impl/control/PrinterMapControlihm
 */

import DrawRegular from 'ol-ext/interaction/DrawRegular';
import { getValue } from '../../../facade/js/i18n/language';

export default class PrinterMapControlihm extends M.impl.Control {
  /**
   * @classdesc
   * Main constructor of the measure conrol.
   *
   * @constructor
   * @extends {ol.control.Control}
   * @api stable
   */
  constructor(mapProxyUrl, ENCurl, ENCLayer) {
    super();
    /**
     * Facade of the map
     * @private
     * @type {M.Map}
     */
    this.facadeMap_ = null;
    this.mapProxyUrl_ = mapProxyUrl;
    this.ENCurl_ = ENCurl;
    this.ENCLayer_ = ENCLayer;
    this.proj4Def_ = '+proj=merc +lat_ts=lat_center +lon_0=0 +datum=WGS84 +units=m +no_defs';
  }

  /**
   * This function adds the control to the specified map
   *
   * @public
   * @function
   * @param {M.Map} map to add the plugin
   * @param {function} template template of this control
   * @api stable
   */
  addTo(map, element) {
    this.facadeMap_ = map;
    super.addTo(map, element);
    // ol.control.Control.call(this, {
    //   element,
    //   target: null,
    // });
    map.getMapImpl().addControl(this);

    /**
     * OL vector source for draw interactions.
     * @private
     * @type {*} - OpenLayers vector source
     */
    this.vectorSource = undefined;
  }

  setSource() {
    this.vectorSource = this.newVectorSource(false);
  }

  /**
   * Creates new OpenLayers vector source
   * @public
   * @function
   * @api
   * @param {Boolean} featuresIncluded - indicates if an OL collection of
   * features should be included in new source
   */
  newVectorSource(featuresIncluded) {
    return featuresIncluded ?
      new ol.source.Vector({ features: new ol.Collection([]) }) :
      new ol.source.Vector();
  }
  /**
   * This function encodes a layer.
   *
   * @public
   * @function
   * @param {Layer} layer to encode
   * @api stable
   */
  getParametrizedLayers(paramName, layers) {
    let others = this.facadeMap_.getMapImpl().getLayers().getArray().filter((layer) => {
      return !M.utils.isNullOrEmpty(layer.getSource()) &&
        // eslint-disable-next-line no-underscore-dangle
        !M.utils.isNullOrEmpty(layer.getSource().params_) &&
        layer.getSource().getParams()[paramName] !== undefined;
    });

    others = others.filter((layer) => {
      return !(layers.some((l) => {
        return l.url !== undefined && l.url === layer.getSource().getUrl();
      }));
    });

    return others;
  }

  /**
   * This function encodes a layer.
   *
   * @public
   * @function
   * @param {Layer} layer to encode
   * @api stable
   */
  encodeLayer(layer, bbox, projection) {
    return (new Promise((success, fail) => {
      if (layer.type === M.layer.type.WMC) {
        // none
      } else if (layer.type === M.layer.type.KML) {
        success(this.encodeKML(layer, bbox, projection, this.getCenterByBbox(bbox)[1]));
      } else if (layer.type === M.layer.type.WMS) {
        success(this.encodeWMS(layer, projection, this.getCenterByBbox(bbox)[1]));
      } else if (layer.type === M.layer.type.WFS) {
        success(this.encodeWFS(layer, bbox, projection));
      } else if (layer.type === M.layer.type.GeoJSON) {
        success(this.encodeWFS(layer, bbox, projection));
      } else if (layer.type === M.layer.type.WMTS) {
        if (projection === 'EPSG:33952') {
          success(this.encodeWMTSToWMS(layer, this.getCenterByBbox(bbox)[1]));
        } else {
          success(this.encodeWMTS(layer));
        }
      } else if (layer instanceof M.layer.Vector) {
        success(this.encodeWFS(layer, bbox, projection));
        // eslint-disable-next-line no-underscore-dangle
      } else if (layer.type === undefined && layer.className_ === 'ol-layer') {
        success(this.encodeImage(layer));
      } else if ([M.layer.type.XYZ, M.layer.type.TMS, M.layer.type.OSM].indexOf(layer.type) > -1) {
        success(this.encodeXYZ(layer));
      } else if (layer.type === M.layer.type.MVT) {
        success(this.encodeMVT(layer));
      } else {
        success(this.encodeWFS(layer, bbox));
      }
    }));
  }

  /**
   * This function adds the control to the specified map
   *
   * @public
   * @function
   * @param {M.Map} map to add the plugin
   * @param {function} template template of this control
   * @api stable
   */
  encodeVector(layer, bboxFeat, projection, latCenter) {
    let encodedLayer = null;

    const olLayer = layer.getImpl().getOL3Layer();
    const features = olLayer.getSource().getFeatures();
    const layerName = layer.name;
    const layerOpacity = olLayer.getOpacity();
    const geoJSONFormat = new ol.format.GeoJSON();
    let bbox = this.facadeMap_.getBbox();
    bbox = bboxFeat || [bbox.x.min, bbox.y.min, bbox.x.max, bbox.y.max];
    const resolution = this.facadeMap_.getMapImpl().getView().getResolution();

    const encodedFeatures = [];
    let indexText = 1;
    let indexGeom = 1;
    let index = 1;
    let style = '';
    const stylesNames = {};
    const stylesNamesText = {};
    let nameFeature;
    let filter;

    features.forEach((feature) => {
      const geometry = feature.getGeometry();
      let styleId = feature.get('styleUrl');
      if (!M.utils.isNullOrEmpty(styleId)) {
        styleId = styleId.replace('#', '');
      }
      const styleFn = feature.getStyle();
      if (!M.utils.isNullOrEmpty(styleFn)) {
        let featureStyle;
        try {
          featureStyle = styleFn(feature, resolution)[0];
        } catch (e) {
          featureStyle = styleFn.call(feature, resolution)[0];
        }
        if (!M.utils.isNullOrEmpty(featureStyle)) {
          const img = featureStyle.getImage();
          let imgSize = img.getImageSize();
          if (M.utils.isNullOrEmpty(imgSize)) {
            imgSize = [64, 64];
          }

          let parseType;
          if (feature.getGeometry().getType().toLowerCase() === 'multipolygon') {
            parseType = 'polygon';
          } else if (feature.getGeometry().getType().toLowerCase() === 'multipoint') {
            parseType = 'point';
          } else {
            parseType = feature.getGeometry().getType().toLowerCase();
          }

          const stroke = featureStyle.getStroke();
          let styleText;
          const styleGeom = {
            id: styleId,
            externalGraphic: img.getSrc(),
            graphicHeight: imgSize[0],
            graphicWidth: imgSize[1],
            graphicOpacity: img.getOpacity(),
            strokeWidth: stroke.getWidth(),
            type: parseType,
          };
          const text = (featureStyle.getText && featureStyle.getText());
          if (!M.utils.isNullOrEmpty(text)) {
            styleText = {
              conflictResolution: 'false',
              fontColor: M.utils.isNullOrEmpty(text.getFill()) ? '' : M.utils.rgbToHex(M.utils.isArray(text.getFill().getColor()) ?
                `rgba(${text.getFill().getColor().toString()})` :
                text.getFill().getColor()),
              fontSize: '11px',
              fontFamily: 'Helvetica, sans-serif',
              fontWeight: 'bold',
              label: M.utils.isNullOrEmpty(text.getText()) ? feature.get('name') : text.getText(),
              labelAlign: text.getTextAlign(),
              labelXOffset: text.getOffsetX(),
              labelYOffset: text.getOffsetY(),
              labelOutlineColor: M.utils.isNullOrEmpty(text.getStroke()) ? '' : M.utils.rgbToHex(M.utils.isArray(text.getStroke().getColor()) ?
                `rgba(${text.getStroke().getColor().toString()})` :
                text.getStroke().getColor()),
              labelOutlineWidth: M.utils.isNullOrEmpty(text.getStroke()) ? '' : text.getStroke().getWidth(),
              type: 'text',
            };
            styleText.fontColor = styleText.fontColor.slice(0, 7);
            styleText.labelOutlineColor = styleText.labelOutlineColor.slice(0, 7);
          }

          nameFeature = `draw${index}`;

          if ((!M.utils.isNullOrEmpty(geometry) && geometry.intersectsExtent(bbox)) ||
            !M.utils.isNullOrEmpty(text)) {
            const styleStr = JSON.stringify(styleGeom);
            const styleTextStr = JSON.stringify(styleText);
            let styleName = stylesNames[styleStr];
            let styleNameText = stylesNamesText[styleTextStr];

            if (M.utils.isUndefined(styleName) || M.utils.isUndefined(styleNameText)) {
              const symbolizers = [];
              let flag = 0;
              if (!M.utils.isNullOrEmpty(geometry) && geometry.intersectsExtent(bbox) &&
                M.utils.isUndefined(styleName)) {
                styleName = indexGeom;
                stylesNames[styleStr] = styleName;
                flag = 1;
                symbolizers.push(styleStr);
                indexGeom += 1;
                index += 1;
              }
              if (!M.utils.isNullOrEmpty(text) && M.utils.isUndefined(styleNameText)) {
                styleNameText = indexText;
                stylesNamesText[styleTextStr] = styleNameText;
                symbolizers.push(styleTextStr);
                indexText += 1;
                if (flag === 0) {
                  index += 1;
                  symbolizers.push(styleStr);
                }
              }

              if (styleName === undefined) {
                styleName = 0;
              }
              if (styleNameText === undefined) {
                styleNameText = 0;
              }

              filter = `"[_gx_style ='${styleName + styleNameText}']"`;

              if (!M.utils.isNullOrEmpty(symbolizers)) {
                const a = `${filter}: {"symbolizers": [${symbolizers}]}`;
                if (style !== '') {
                  style += `,${a}`;
                } else {
                  style += `{${a}, "version": "2"`;
                }
              }
            }

            const geoJSONFeature = geoJSONFormat.writeFeatureObject(feature);
            geoJSONFeature.properties = {
              name: nameFeature,
              _gx_style: styleName + styleNameText,
            };
            if (projection === 'EPSG:33952') {
              const proj4Str = this.proj4Def_.replace('lat_center', latCenter);
              geoJSONFeature.geometry.coordinates =
                this.transformCoords(proj4Str, geoJSONFeature.geometry.coordinates);
            }
            encodedFeatures.push(geoJSONFeature);
          }
        }
      }
    }, this);

    if (style !== '') {
      style = JSON.parse(style.concat('}'));
    } else {
      style = {
        '*': {
          symbolizers: [],
        },
        version: '2',
      };
    }

    encodedLayer = {
      type: 'Vector',
      style,
      // styleProperty: '_gx_style',
      geoJson: {
        type: 'FeatureCollection',
        features: encodedFeatures,
      },
      name: layerName,
      opacity: layerOpacity,
    };

    return encodedLayer;
  }
  encodeKML(layer, bboxFeat, projection, latCenter) {
    let encodedLayer = null;

    const olLayer = layer.getImpl().getOL3Layer();
    const features = olLayer.getSource().getFeatures();
    const layerName = layer.name;
    const layerOpacity = olLayer.getOpacity();
    const geoJSONFormat = new ol.format.GeoJSON();
    let bbox = this.facadeMap_.getBbox();
    bbox = bboxFeat || [bbox.x.min, bbox.y.min, bbox.x.max, bbox.y.max];
    const resolution = this.facadeMap_.getMapImpl().getView().getResolution();

    const encodedFeatures = [];
    let indexText = 1;
    let indexGeom = 1;
    let index = 1;
    let style = '';
    const stylesNames = {};
    const stylesNamesText = {};
    let nameFeature;
    let filter;

    features.forEach((feature) => {
      const geometry = feature.getGeometry();
      let styleId = feature.get('styleUrl');
      if (!M.utils.isNullOrEmpty(styleId)) {
        styleId = styleId.replace('#', '');
      }
      const styleFn = feature.getStyle();
      if (!M.utils.isNullOrEmpty(styleFn)) {
        let featureStyle;
        try {
          featureStyle = styleFn(feature, resolution)[0];
        } catch (e) {
          featureStyle = styleFn.call(feature, resolution)[0];
        }
        if (!M.utils.isNullOrEmpty(featureStyle)) {
          const img = featureStyle.getImage();
          let imgSize = img.getImageSize();
          if (M.utils.isNullOrEmpty(imgSize)) {
            imgSize = [64, 64];
          }

          let parseType;
          if (feature.getGeometry().getType().toLowerCase() === 'multipolygon') {
            parseType = 'polygon';
          } else if (feature.getGeometry().getType().toLowerCase() === 'multipoint') {
            parseType = 'point';
          } else {
            parseType = feature.getGeometry().getType().toLowerCase();
          }

          const stroke = featureStyle.getStroke();
          let styleText;
          const styleGeom = {
            id: styleId,
            externalGraphic: img.getSrc(),
            graphicHeight: imgSize[0],
            graphicWidth: imgSize[1],
            graphicOpacity: img.getOpacity(),
            strokeWidth: stroke.getWidth(),
            type: parseType,
          };
          const text = (featureStyle.getText && featureStyle.getText());
          if (!M.utils.isNullOrEmpty(text)) {
            styleText = {
              conflictResolution: 'false',
              fontColor: M.utils.isNullOrEmpty(text.getFill()) ? '' : M.utils.rgbToHex(M.utils.isArray(text.getFill().getColor()) ?
                `rgba(${text.getFill().getColor().toString()})` :
                text.getFill().getColor()),
              fontSize: '11px',
              fontFamily: 'Helvetica, sans-serif',
              fontWeight: 'bold',
              label: M.utils.isNullOrEmpty(text.getText()) ? feature.get('name') : text.getText(),
              labelAlign: text.getTextAlign(),
              labelXOffset: text.getOffsetX(),
              labelYOffset: text.getOffsetY(),
              labelOutlineColor: M.utils.isNullOrEmpty(text.getStroke()) ? '' : M.utils.rgbToHex(M.utils.isArray(text.getStroke().getColor()) ?
                `rgba(${text.getStroke().getColor().toString()})` :
                text.getStroke().getColor()),
              labelOutlineWidth: M.utils.isNullOrEmpty(text.getStroke()) ? '' : text.getStroke().getWidth(),
              type: 'text',
            };
            styleText.fontColor = styleText.fontColor.slice(0, 7);
            styleText.labelOutlineColor = styleText.labelOutlineColor.slice(0, 7);
          }

          nameFeature = `draw${index}`;

          if ((!M.utils.isNullOrEmpty(geometry) && geometry.intersectsExtent(bbox)) ||
            !M.utils.isNullOrEmpty(text)) {
            const styleStr = JSON.stringify(styleGeom);
            const styleTextStr = JSON.stringify(styleText);
            let styleName = stylesNames[styleStr];
            let styleNameText = stylesNamesText[styleTextStr];

            if (M.utils.isUndefined(styleName) || M.utils.isUndefined(styleNameText)) {
              const symbolizers = [];
              let flag = 0;
              if (!M.utils.isNullOrEmpty(geometry) && geometry.intersectsExtent(bbox) &&
                M.utils.isUndefined(styleName)) {
                styleName = indexGeom;
                stylesNames[styleStr] = styleName;
                flag = 1;
                symbolizers.push(styleStr);
                indexGeom += 1;
                index += 1;
              }
              if (!M.utils.isNullOrEmpty(text) && M.utils.isUndefined(styleNameText)) {
                styleNameText = indexText;
                stylesNamesText[styleTextStr] = styleNameText;
                symbolizers.push(styleTextStr);
                indexText += 1;
                if (flag === 0) {
                  index += 1;
                  symbolizers.push(styleStr);
                }
              }

              if (styleName === undefined) {
                styleName = 0;
              }
              if (styleNameText === undefined) {
                styleNameText = 0;
              }

              filter = `"[_gx_style ='${styleName + styleNameText}']"`;

              if (!M.utils.isNullOrEmpty(symbolizers)) {
                const a = `${filter}: {"symbolizers": [${symbolizers}]}`;
                if (style !== '') {
                  style += `,${a}`;
                } else {
                  style += `{${a}, "version": "2"`;
                }
              }
            }

            const geoJSONFeature = geoJSONFormat.writeFeatureObject(feature);
            geoJSONFeature.properties = {
              name: nameFeature,
              _gx_style: styleName + styleNameText,
            };
            if (projection === 'EPSG:33952') {
              const proj4Str = this.proj4Def_.replace('lat_center', latCenter);
              geoJSONFeature.geometry.coordinates =
                this.transformCoords(proj4Str, geoJSONFeature.geometry.coordinates);
            }
            encodedFeatures.push(geoJSONFeature);
          }
        }
      }
    }, this);

    if (style !== '') {
      style = JSON.parse(style.concat('}'));
    } else {
      style = {
        '*': {
          symbolizers: [],
        },
        version: '2',
      };
    }

    encodedLayer = {
      type: 'Vector',
      style,
      // styleProperty: '_gx_style',
      geoJson: {
        type: 'FeatureCollection',
        features: encodedFeatures,
      },
      name: layerName,
      opacity: layerOpacity,
    };

    return encodedLayer;
  }

  /**
   * This function encodes a WMS layer.
   *
   * @public
   * @function
   * @param {M.layer.WMS} layer to encode
   * @api stable
  */
  encodeWMS(layer, projection, centerLat) {
    let encodedLayer = null;
    const olLayer = layer.getImpl().getOL3Layer();
    let layerUrl = layer.url;
    let layerENC = this.ENCLayer_;
    if (layer.name === 'ENC') {
      layerUrl = this.ENCurl_;
    }
    if (projection === 'EPSG:33952') {
      layerUrl = this.mapProxyUrl_;
      layerENC = layer.url.indexOf('enccache') > 0 ? layerENC.concat('_7cs') : layer.name;
    }
    const layerOpacity = olLayer.getOpacity();
    const params = olLayer.getSource().getParams();
    const paramsLayers = ((layer.name === 'ENC') || (layerENC.indexOf('_7cs') > 0)) ? [layerENC] : [params.LAYERS];
    // const paramsFormat = params.FORMAT;
    const paramsStyles = [params.STYLES];
    encodedLayer = {
      baseURL: layerUrl,
      opacity: layerOpacity,
      type: 'WMS',
      layers: paramsLayers.join(',').split(','),
      // format: paramsFormat || 'image/jpeg',
      styles: paramsStyles.join(',').split(','),
    };

    /** ***********************************
     MAPEO DE CAPAS TILEADA.
    ************************************ */
    // eslint-disable-next-line no-underscore-dangle
    if (layer._updateNoCache) {
      // eslint-disable-next-line no-underscore-dangle
      layer._updateNoCache();
      const noCacheName = layer.getNoCacheName();
      const noChacheUrl = layer.getNoCacheUrl();
      if (!M.utils.isNullOrEmpty(noCacheName) && !M.utils.isNullOrEmpty(noChacheUrl)) {
        encodedLayer.layers = [noCacheName];
        encodedLayer.baseURL = noChacheUrl;
      }
    } else {
      const noCacheName = layer.getNoChacheName();
      const noCacheUrl = layer.getNoChacheUrl();
      if (!M.utils.isNullOrEmpty(noCacheName) && !M.utils.isNullOrEmpty(noCacheUrl)) {
        encodedLayer.layers = [noCacheName];
        encodedLayer.baseURL = noCacheUrl;
      }
    }

    /** *********************************  */

    // defaults
    encodedLayer.customParams = {
      center_epsg: centerLat,
    };

    const propKeys = Object.keys(params);
    propKeys.forEach((key) => {
      if ('iswmc,transparent'.indexOf(key.toLowerCase()) !== -1) {
        encodedLayer.customParams[key] = params[key];
      }
    });
    return encodedLayer;
  }

  /**
   * This function encodes a OL Image layer.
   *
   * @public
   * @function
   * @param {IMAGE} layer to encode
   * @api stable
   */
  encodeImage(layer) {
    let encodedLayer = null;
    const olLayer = layer;
    const params = olLayer.getSource().getParams();
    const paramsLayers = [params.LAYERS];
    const paramsStyles = [params.STYLES];
    encodedLayer = {
      baseURL: olLayer.getSource().getUrl(),
      opacity: olLayer.getOpacity(),
      type: 'WMS',
      layers: paramsLayers.join(',').split(','),
      // format: params.FORMAT || 'image/jpeg',
      styles: paramsStyles.join(',').split(','),
    };

    encodedLayer.customParams = {
      IMAGEN: params.IMAGEN,
      transparent: true,
      iswmc: false,
    };

    return encodedLayer;
  }

  encodeXYZ(layer) {
    const layerImpl = layer.getImpl();
    const olLayer = layerImpl.getOL3Layer();
    const layerSource = olLayer.getSource();
    const tileGrid = layerSource.getTileGrid();
    let layerUrl = layer.url;
    const layerOpacity = olLayer.getOpacity();
    const layerExtent = tileGrid.getExtent();
    const tileSize = tileGrid.getTileSize();
    const resolutions = tileGrid.getResolutions();

    if (layer.type === M.layer.type.OSM) {
      layerUrl = layer.url || 'http://tile.openstreetmap.org/';
    }

    return {
      opacity: layerOpacity,
      baseURL: layerUrl,
      maxExtent: layerExtent,
      tileSize: [tileSize, tileSize],
      resolutions,
      type: 'osm',
    };
  }

  encodeMVT(layer) {
    let encodedLayer = null;
    // const projection = this.facadeMap_.getProjection();
    const features = layer.getFeatures();
    const layerName = layer.name;
    const layerOpacity = layer.getOpacity();
    const layerStyle = layer.getImpl().getOL3Layer().getStyle();
    let bbox = this.facadeMap_.getBbox();
    bbox = [bbox.x.min, bbox.y.min, bbox.x.max, bbox.y.max];
    const resolution = this.facadeMap_.getMapImpl().getView().getResolution();
    const encodedFeatures = [];
    let nameFeature;
    let filter;
    let index = 1;
    let indexText = 1;
    let indexGeom = 1;
    let style = '';
    const stylesNames = {};
    const stylesNamesText = {};
    features.forEach((feature) => {
      const geometry = feature.getImpl().getOLFeature().getGeometry();
      let featureStyle;
      const fStyle = feature.getImpl().getOLFeature().getStyleFunction();
      if (!M.utils.isNullOrEmpty(fStyle)) {
        featureStyle = fStyle;
      } else if (!M.utils.isNullOrEmpty(layerStyle)) {
        featureStyle = layerStyle;
      }

      if (featureStyle instanceof Function) {
        featureStyle = featureStyle.call(featureStyle, feature, resolution);
      }

      if (featureStyle instanceof Array) {
        // SRC style has priority
        if (featureStyle.length > 1) {
          featureStyle = (!M.utils.isNullOrEmpty(featureStyle[1].getImage()) &&
            featureStyle[1].getImage().getSrc) ?
            featureStyle[1] : featureStyle[0];
        } else {
          featureStyle = featureStyle[0];
        }
      }

      if (!M.utils.isNullOrEmpty(featureStyle)) {
        const image = featureStyle.getImage();
        const imgSize = M.utils
          .isNullOrEmpty(image) ? [0, 0] : (image.getImageSize() || [24, 24]);
        let text = featureStyle.getText();
        if (M.utils.isNullOrEmpty(text) && !M.utils.isNullOrEmpty(featureStyle.textPath)) {
          text = featureStyle.textPath;
        }

        let parseType;
        if (geometry.getType().toLowerCase() === 'multipolygon') {
          parseType = 'polygon';
        } else if (geometry.getType().toLowerCase() === 'multipoint') {
          parseType = 'point';
        } else if (geometry.getType().toLowerCase().indexOf('linestring') > -1) {
          parseType = 'line';
        } else {
          parseType = geometry.getType().toLowerCase();
        }

        const stroke = M.utils.isNullOrEmpty(image) ?
          featureStyle.getStroke() : (image.getStroke && image.getStroke());
        const fill = M.utils.isNullOrEmpty(image) ?
          featureStyle.getFill() : (image.getFill && image.getFill());


        let styleText;
        const lineDash = (featureStyle.getStroke() !== null &&
          featureStyle.getStroke() !== undefined) ?
          featureStyle.getStroke().getLineDash() : undefined;
        const styleGeom = {
          type: parseType,
          fillColor: M.utils.isNullOrEmpty(fill) || (layer.name.indexOf(' Reverse') > -1 && layer.name.indexOf('Cobertura') > -1) ? '#000000' : M.utils.rgbaToHex(fill.getColor()).slice(0, 7),
          fillOpacity: M.utils.isNullOrEmpty(fill) ?
            0 : M.utils.getOpacityFromRgba(fill.getColor()),
          strokeColor: M.utils.isNullOrEmpty(stroke) ? '#000000' : M.utils.rgbaToHex(stroke.getColor()),
          strokeOpacity: M.utils.isNullOrEmpty(stroke) ?
            0 : M.utils.getOpacityFromRgba(stroke.getColor()),
          strokeWidth: M.utils.isNullOrEmpty(stroke) ? 0 : (stroke.getWidth && stroke.getWidth()),
          pointRadius: M.utils.isNullOrEmpty(image) ? '' : (image.getRadius && image.getRadius()),
          externalGraphic: M.utils.isNullOrEmpty(image) ? '' : (image.getSrc && image.getSrc()),
          graphicHeight: imgSize[0],
          graphicWidth: imgSize[1],
          strokeLinecap: 'round',
        };

        if (layer.name === 'coordinateresult') {
          styleGeom.fillOpacity = 1;
          styleGeom.strokeOpacity = 1;
          styleGeom.fillColor = '#ffffff';
          styleGeom.strokeColor = '#ff0000';
          styleGeom.strokeWidth = 2;
        }

        if (layer.name === 'infocoordinatesLayerFeatures') {
          styleGeom.fillColor = '#ffffff';
          styleGeom.fillOpacity = 1;
          styleGeom.strokeWidth = 1;
          styleGeom.strokeColor = '#2690e7';
          styleGeom.strokeOpacity = 1;
          styleGeom.graphicName = 'cross';
          styleGeom.graphicWidth = 15;
          styleGeom.graphicHeight = 15;
        }

        if (layer.name.indexOf(' Reverse') > -1 && layer.name.indexOf('Cobertura') > -1) {
          styleGeom.fillColor = styleGeom.strokeColor;
          styleGeom.fillOpacity = 0.5;
        }

        if (lineDash !== undefined && lineDash !== null && lineDash.length > 0) {
          if (lineDash[0] === 1 && lineDash.length === 2) {
            styleGeom.strokeDashstyle = 'dot';
          } else if (lineDash[0] === 10) {
            styleGeom.strokeDashstyle = 'dash';
          } else if (lineDash[0] === 1 && lineDash.length > 2) {
            styleGeom.strokeDashstyle = 'dashdot';
          }
        }

        if (!M.utils.isNullOrEmpty(text)) {
          let tAlign = text.getTextAlign();
          let tBLine = text.getTextBaseline();
          let align = '';
          if (!M.utils.isNullOrEmpty(tAlign)) {
            if (tAlign === M.style.align.LEFT) {
              tAlign = 'l';
            } else if (tAlign === M.style.align.RIGHT) {
              tAlign = 'r';
            } else if (tAlign === M.style.align.CENTER) {
              tAlign = 'c';
            } else {
              tAlign = '';
            }
          }
          if (!M.utils.isNullOrEmpty(tBLine)) {
            if (tBLine === M.style.baseline.BOTTOM) {
              tBLine = 'b';
            } else if (tBLine === M.style.baseline.MIDDLE) {
              tBLine = 'm';
            } else if (tBLine === M.style.baseline.TOP) {
              tBLine = 't';
            } else {
              tBLine = '';
            }
          }
          if (!M.utils.isNullOrEmpty(tAlign) && !M.utils.isNullOrEmpty(tBLine)) {
            align = tAlign.concat(tBLine);
          }
          const font = text.getFont();
          const fontWeight = !M.utils.isNullOrEmpty(font) && font.indexOf('bold') > -1 ? 'bold' : 'normal';
          let fontSize = '11px';
          if (!M.utils.isNullOrEmpty(font)) {
            const px = font.substr(0, font.indexOf('px'));
            if (!M.utils.isNullOrEmpty(px)) {
              const space = px.lastIndexOf(' ');
              if (space > -1) {
                fontSize = px.substr(space, px.length).trim().concat('px');
              } else {
                fontSize = px.concat('px');
              }
            }
          }

          styleText = {
            type: 'text',
            label: text.getText(),
            fontColor: M.utils.isNullOrEmpty(text.getFill()) ? '#000000' : M.utils.rgbToHex(text.getFill().getColor()),
            fontSize,
            fontFamily: 'Helvetica, sans-serif',
            fontStyle: 'normal',
            fontWeight,
            conflictResolution: 'false',
            labelXOffset: text.getOffsetX(),
            labelYOffset: text.getOffsetY(),
            fillColor: styleGeom.fillColor || '#FF0000',
            fillOpacity: styleGeom.fillOpacity || 1,
            labelOutlineColor: M.utils.isNullOrEmpty(text.getStroke()) ? '' : M.utils.rgbToHex(text.getStroke().getColor() || '#FF0000'),
            labelOutlineWidth: M.utils.isNullOrEmpty(text.getStroke()) ? '' : text.getStroke().getWidth(),
            labelAlign: align,
          };
        } else if (layer.name === 'infocoordinatesLayerFeatures') {
          text = true;
          styleText = {
            type: 'text',
            conflictResolution: 'false',
            fontFamily: 'Helvetica, sans-serif',
            fontStyle: 'normal',
            fontColor: '#ffffff',
            fontSize: '12px',
            label: `${feature.getId()}`,
            labelAlign: 'lb',
            labelXOffset: '4',
            labelYOffset: '3',
            haloColor: '#2690e7',
            haloRadius: '1',
            haloOpacity: '1',
          };
        }

        nameFeature = `draw${index}`;
        const extent = geometry.getExtent();
        if ((!M.utils.isNullOrEmpty(geometry) && ol.extent.intersects(bbox, extent)) ||
          !M.utils.isNullOrEmpty(text)) {
          const styleStr = JSON.stringify(styleGeom);
          const styleTextStr = JSON.stringify(styleText);
          let styleName = stylesNames[styleStr];
          let styleNameText = stylesNamesText[styleTextStr];

          if (M.utils.isUndefined(styleName) || M.utils.isUndefined(styleNameText)) {
            const symbolizers = [];
            let flag = 0;
            if (!M.utils.isNullOrEmpty(geometry) && ol.extent.intersects(bbox, extent) &&
              M.utils.isUndefined(styleName)) {
              styleName = indexGeom;
              stylesNames[styleStr] = styleName;
              flag = 1;
              symbolizers.push(styleStr);
              indexGeom += 1;
              index += 1;
            }
            if (!M.utils.isNullOrEmpty(text) && M.utils.isUndefined(styleNameText)) {
              styleNameText = indexText;
              stylesNamesText[styleTextStr] = styleNameText;
              symbolizers.push(styleTextStr);
              indexText += 1;
              if (flag === 0) {
                index += 1;
                symbolizers.push(styleStr);
              }
            }
            if (styleName === undefined) {
              styleName = 0;
            }
            if (styleNameText === undefined) {
              styleNameText = 0;
            }
            filter = `"[_gx_style ='${styleName + styleNameText}']"`;
            if (!M.utils.isNullOrEmpty(symbolizers)) {
              const a = `${filter}: {"symbolizers": [${symbolizers}]}`;
              if (style !== '') {
                style += `,${a}`;
              } else {
                style += `{${a},"version":"2"`;
              }
            }
          }

          let coordinates = geometry.getFlatCoordinates();
          coordinates = this.inflateCoordinatesArray(coordinates, 0, geometry.getEnds(), 2);
          const geoJSONFeature = {
            id: feature.getId(),
            type: 'Feature',
            geometry: {
              type: geometry.getType(),
              coordinates,
            },
          };

          /*
          if (projection.code !== 'EPSG:3857' && this.facadeMap_.getLayers().some(layerParam =>
          (layerParam.type === M.layer.type.OSM || layerParam.type === M.layer.type.Mapbox))) {
            geoJSONFeature = geoJSONFormat.writeFeatureObject(feature.getImpl().getOLFeature(), {
              featureProjection: projection.code,
              dataProjection: 'EPSG:3857',
            });
          } else {
            geoJSONFeature = geoJSONFormat.writeFeatureObject(feature.getImpl().getOLFeature());
          }
          */

          geoJSONFeature.properties = {
            _gx_style: styleName + styleNameText,
            name: nameFeature,
          };
          encodedFeatures.push(geoJSONFeature);
        }
      }
    }, this);

    if (style !== '') {
      style = JSON.parse(style.concat('}'));
    } else {
      style = {
        '*': {
          symbolizers: [],
        },
        version: '2',
      };
    }

    encodedLayer = {
      type: 'Vector',
      style,
      // styleProperty: '_gx_style',
      geoJson: {
        type: 'FeatureCollection',
        features: encodedFeatures,
      },
      name: layerName,
      opacity: layerOpacity,
    };

    return encodedLayer;
  }

  /**
   * This function adds the control to the specified map
   *
   * @public
   * @function
   * @param {M.Map} map to add the plugin
   * @param {function} template template of this control
   * @api stable
   */
  encodeWFS(layer, bboxFeat, projection) {
    let encodedLayer = null;
    const olLayer = layer.getImpl().getOL3Layer();
    const features = olLayer.getSource().getFeatures();
    const layerName = layer.name;
    const layerOpacity = olLayer.getOpacity();
    const latCenter = this.getCenterByBbox(bboxFeat)[1];
    let encodedFeatures = [];
    let style = '';
    let index = 1;
    let indexStyle = 1;
    let indexGeom = 1;
    const stylesNames = {};
    const stylesNamesText = {};
    features.forEach((feature) => {
      let encodedFeature = null;
      if (feature.getGeometry().getType().toLowerCase() === 'geometrycollection') {
        encodedFeature =
          this.encodeGeometryCollection_(layer, feature, style, index, indexGeom, latCenter);
        if (encodedFeature.geojson.length > 0) {
          encodedFeatures = encodedFeatures.concat(encodedFeature.geojson);
          style += encodedFeature.style;
          index += encodedFeature.plusIndex;
          indexGeom += encodedFeature.plusIndexGeom;
        }
      } else {
        encodedFeature = this.encodeFeature_(
          layer, feature, style, index, indexStyle, projection,
          stylesNames, stylesNamesText, bboxFeat, latCenter,
        );

        if (encodedFeature.geojson !== null) {
          encodedFeatures.push(encodedFeature.geojson);
          style = encodedFeature.style;
          index += 1;
          indexStyle += encodedFeature.plusIndexStyle;
        }
      }
    }, this);
    if (style !== '') {
      style = JSON.parse(style.concat('}'));
    } else {
      style = {
        '*': {
          symbolizers: [],
        },
        version: '2',
      };
    }

    encodedLayer = {
      type: 'Vector',
      style,
      geoJson: {
        type: 'FeatureCollection',
        features: encodedFeatures,
      },
      name: layerName,
      opacity: layerOpacity,
    };

    return encodedLayer;
  }

  encodeFeature_(
    layer, feature, style, index, indexStyle, projectionPrint,
    stylesNames, stylesNamesText, bboxFeat, latCenter,
  ) {
    let res = null;
    const mapProjection = this.facadeMap_.getProjection();
    // const olLayer = layer.getImpl().getOL3Layer();
    // const layerStyle = olLayer.getStyle();
    const geoJSONFormat = new ol.format.GeoJSON();
    let bbox = this.facadeMap_.getBbox();
    bbox = bboxFeat || [bbox.x.min, bbox.y.min, bbox.x.max, bbox.y.max];
    const resolution = this.facadeMap_.getMapImpl().getView().getResolution();
    const nameFeature = `draw${index}`;
    let filter;
    let plusIndexStyle = 0;
    const geometry = feature.getGeometry();
    let fStyle = feature.getStyle();
    let newStyle = style;
    const styleIds = [];
    let includeFeature = false;
    if (!M.utils.isNullOrEmpty(fStyle) && fStyle instanceof Function) {
      fStyle = fStyle.call(fStyle, feature, resolution);
    }

    if (!M.utils.isArray(fStyle)) {
      fStyle = [fStyle];
    }

    fStyle.forEach((featureStyle) => {
      const image = featureStyle.getImage();
      const imgSize = [].concat(M.utils.isNullOrEmpty(image) ? [0, 0] :
        (image.getImageSize() || [24, 24]));
      let text = featureStyle.getText();
      if (M.utils.isNullOrEmpty(text) && !M.utils.isNullOrEmpty(featureStyle.textPath)) {
        text = featureStyle.textPath;
      }
      let parseType;
      if (feature.getGeometry().getType().toLowerCase() === 'multipolygon') {
        parseType = 'polygon';
      } else if (feature.getGeometry().getType().toLowerCase() === 'multipoint') {
        parseType = 'point';
      } else if (feature.getGeometry().getType().toLowerCase().indexOf('linestring') > -1) {
        parseType = 'line';
      } else {
        parseType = feature.getGeometry().getType().toLowerCase();
      }

      const stroke = M.utils.isNullOrEmpty(image) ?
        featureStyle.getStroke() : (image.getStroke && image.getStroke());
      if (!M.utils.isNullOrEmpty(stroke) && stroke.getColor() === 'transparent') {
        stroke.setColor('rgba(0, 0, 0, 0)');
      }
      const fill = M.utils.isNullOrEmpty(image) ? featureStyle.getFill() :
        (image.getFill && image.getFill());
      if (!M.utils.isNullOrEmpty(fill) && fill.getColor() === 'transparent') {
        fill.setColor('rgba(0, 0, 0, 0)');
      }

      let styleText;
      const lineDash = (featureStyle.getStroke() !== null &&
        featureStyle.getStroke() !== undefined) ?
        featureStyle.getStroke().getLineDash() : undefined;
      const offset = this.getImageOffset(image, imgSize);
      const imageSrc = !M.utils.isNullOrEmpty(image) && image.getSrc ? image.getSrc() : '';
      if (imageSrc.endsWith('ais.svg') || imageSrc.endsWith('racon.svg') || imageSrc.endsWith('rc.svg') || imageSrc.endsWith('dgps.svg')) {
        imgSize[0] *= 0.7;
        imgSize[1] *= 0.7;
        offset.x *= 1;
        offset.y *= 1;
      }
      const styleGeom = {
        type: parseType,
        fillColor: M.utils.isNullOrEmpty(fill) || (layer.name && layer.name.indexOf(' Reverse') > -1 && layer.name.indexOf('Cobertura') > -1) ? '#000000' : M.utils.rgbaToHex(fill.getColor()).slice(0, 7),
        fillOpacity: M.utils.isNullOrEmpty(fill) ? 0 : M.utils.getOpacityFromRgba(fill.getColor()),
        strokeColor: M.utils.isNullOrEmpty(stroke) ? '#000000' :
          M.utils.rgbaToHex(stroke.getColor()),
        strokeOpacity: M.utils.isNullOrEmpty(stroke) ? 0 :
          M.utils.getOpacityFromRgba(stroke.getColor()),
        strokeWidth: M.utils.isNullOrEmpty(stroke) ? 0 : (stroke.getWidth && stroke.getWidth()),
      };

      if (!M.utils.isNullOrEmpty(imageSrc)) {
        styleGeom.pointRadius = M.utils.isNullOrEmpty(image) ? '' : (image.getRadius && image.getRadius());
        styleGeom.externalGraphic = imageSrc;
        styleGeom.graphicXOffset = offset.x;
        styleGeom.graphicYOffset = offset.y;
        styleGeom.graphicHeight = imgSize[1];
        styleGeom.graphicWidth = imgSize[0];
        styleGeom.strokeLinecap = 'round';
        styleGeom.rotation = M.utils.isNullOrEmpty(image) ? 0 :
          (image.getRotation && image.getRotation());

        if (styleGeom.externalGraphic.startsWith('./')) {
          styleGeom.externalGraphic = styleGeom.externalGraphic.replace('./', './imagenes/');
        } else if (layer.name === 'Signals' && styleGeom.externalGraphic.startsWith('data:')
          && Number.isNaN(styleGeom.rotation)) {
          styleGeom.externalGraphic = null;
        }
        // Rotación de radianes a grados
        if (!Number.isNaN(styleGeom.rotation) && styleGeom.rotation !== 0) {
          const factor = 0.10;
          styleGeom.rotation *= 180 / Math.PI;
          styleGeom.graphicXOffset = 186;
          styleGeom.graphicWidth *= factor;
          styleGeom.graphicYOffset = -20;
          styleGeom.graphicHeight *= factor;
          styleGeom.externalGraphic = this.resizeImgData(styleGeom.externalGraphic, factor);
        } else {
          styleGeom.rotation = 0;
        }
      }

      if (layer.name === 'coordinateresult') {
        styleGeom.fillOpacity = 1;
        styleGeom.strokeOpacity = 1;
        styleGeom.fillColor = '#ffffff';
        styleGeom.strokeColor = '#ff0000';
        styleGeom.strokeWidth = 2;
      }

      if (layer.name === 'infocoordinatesLayerFeatures') {
        styleGeom.fillColor = '#ffffff';
        styleGeom.fillOpacity = 1;
        styleGeom.strokeWidth = 1;
        styleGeom.strokeColor = '#2690e7';
        styleGeom.strokeOpacity = 1;
        styleGeom.graphicName = 'cross';
        styleGeom.graphicWidth = 15;
        styleGeom.graphicHeight = 15;
      }

      if (layer.name.indexOf(' Reverse') > -1 && layer.name.indexOf('Cobertura') > -1) {
        styleGeom.fillColor = styleGeom.strokeColor;
        styleGeom.fillOpacity = 0.5;
      }

      if (lineDash !== undefined && lineDash !== null && lineDash.length > 0) {
        if (lineDash[0] === 1 && lineDash.length === 2) {
          styleGeom.strokeDashstyle = 'dot';
        } else if (lineDash[0] === 10) {
          styleGeom.strokeDashstyle = 'dash';
        } else if (lineDash[0] === 1 && lineDash.length > 2) {
          styleGeom.strokeDashstyle = 'dashdot';
        }
      }

      if (!M.utils.isNullOrEmpty(text)) {
        let tAlign = text.getTextAlign();
        let tBLine = text.getTextBaseline();
        let align = '';
        if (!M.utils.isNullOrEmpty(tAlign)) {
          if (tAlign === M.style.align.LEFT) {
            tAlign = 'l';
          } else if (tAlign === M.style.align.RIGHT) {
            tAlign = 'r';
          } else if (tAlign === M.style.align.CENTER) {
            tAlign = 'c';
          } else {
            tAlign = '';
          }
        }
        if (!M.utils.isNullOrEmpty(tBLine)) {
          if (tBLine === M.style.baseline.BOTTOM) {
            tBLine = 'b';
          } else if (tBLine === M.style.baseline.MIDDLE) {
            tBLine = 'm';
          } else if (tBLine === M.style.baseline.TOP) {
            tBLine = 't';
          } else {
            tBLine = '';
          }
        }
        if (!M.utils.isNullOrEmpty(tAlign) && !M.utils.isNullOrEmpty(tBLine)) {
          align = tAlign.concat(tBLine);
        }
        const font = text.getFont();
        const fontWeight = !M.utils.isNullOrEmpty(font) && font.indexOf('bold') > -1 ? 'bold' : 'normal';
        let fontSize = '11px';
        if (!M.utils.isNullOrEmpty(font)) {
          const px = font.substr(0, font.indexOf('px'));
          if (!M.utils.isNullOrEmpty(px)) {
            const space = px.lastIndexOf(' ');
            if (space > -1) {
              fontSize = px.substr(space, px.length).trim().concat('px');
            } else {
              fontSize = px.concat('px');
            }
          }
        }

        styleText = {
          type: 'text',
          label: M.utils.isNullOrEmpty(text.getText()) ? '' : text.getText(),
          fontColor: M.utils.isNullOrEmpty(text.getFill()) ? '#000000' : M.utils.rgbToHex(text.getFill().getColor()),
          fontSize,
          fontFamily: 'Helvetica, sans-serif',
          fontStyle: 'normal',
          fontWeight,
          conflictResolution: 'false',
          labelXOffset: text.getOffsetX(),
          labelYOffset: text.getOffsetY(),
          fillColor: styleGeom.fillColor || '#FF0000',
          fillOpacity: styleGeom.fillOpacity || 1,
          labelOutlineColor: M.utils.isNullOrEmpty(text.getStroke()) ? '' : M.utils.rgbToHex(text.getStroke().getColor() || '#FF0000'),
          labelOutlineWidth: M.utils.isNullOrEmpty(text.getStroke()) ? '' : text.getStroke().getWidth(),
          labelAlign: align,
        };
      } else if (layer.name === 'infocoordinatesLayerFeatures') {
        text = true;
        styleText = {
          type: 'text',
          conflictResolution: 'false',
          fontFamily: 'Helvetica, sans-serif',
          fontStyle: 'normal',
          fontColor: '#ffffff',
          fontSize: '12px',
          label: `${feature.getId()}`,
          labelAlign: 'lb',
          labelXOffset: '4',
          labelYOffset: '3',
          haloColor: '#2690e7',
          haloRadius: '1',
          haloOpacity: '1',
        };
      }

      if (((!M.utils.isNullOrEmpty(geometry) && geometry.intersectsExtent(bbox)) ||
        !M.utils.isNullOrEmpty(text)) && styleGeom.externalGraphic !== null) {
        const styleStr = JSON.stringify(styleGeom);
        const styleTextStr = JSON.stringify(styleText);
        let styleName = stylesNames[styleStr];
        let styleNameText = stylesNamesText[styleTextStr];
        if (M.utils.isUndefined(styleName) || M.utils.isUndefined(styleNameText)) {
          const symbolizers = [];
          let flag = 0;
          if (!M.utils.isNullOrEmpty(geometry) && geometry.intersectsExtent(bbox) &&
            M.utils.isUndefined(styleName)) {
            styleName = indexStyle + plusIndexStyle;
            // eslint-disable-next-line no-param-reassign
            stylesNames[styleStr] = styleName;
            flag = 1;
            symbolizers.push(styleStr);
          }
          if (!M.utils.isNullOrEmpty(text) && M.utils.isUndefined(styleNameText)) {
            styleNameText = indexStyle + plusIndexStyle;
            // eslint-disable-next-line no-param-reassign
            stylesNamesText[styleTextStr] = styleNameText;
            symbolizers.push(styleTextStr);
            if (flag === 0) {
              symbolizers.push(styleStr);
            }
          }

          if (!M.utils.isNullOrEmpty(symbolizers)) {
            styleIds.push(indexStyle + plusIndexStyle);
            filter = `"[_gx_style ='${indexStyle + plusIndexStyle}']"`;
            plusIndexStyle += 1;
            const a = `${filter}: {"symbolizers": [${symbolizers}]}`;
            if (newStyle.indexOf(filter) === -1) {
              if (newStyle !== '') {
                // eslint-disable-next-line no-param-reassign
                newStyle += `,${a}`;
              } else {
                // eslint-disable-next-line no-param-reassign
                newStyle += `{${a},"version":"2"`;
              }
            }
          } else if (!M.utils.isUndefined(styleName)) {
            styleIds.push(styleName);
          }
        }
        includeFeature = true;
      }
    });

    if (includeFeature) {
      let geoJSONFeature;
      if (/* projection.code !== 'EPSG:3857' && */
        this.facadeMap_.getLayers().some(layerParam =>
          (layerParam.type === M.layer.type.OSM || layerParam.type === M.layer.type.Mapbox))) {
        geoJSONFeature = geoJSONFormat.writeFeatureObject(feature, {
          featureProjection: mapProjection.code,
          dataProjection: projectionPrint,
        });
      } else {
        geoJSONFeature = geoJSONFormat.writeFeatureObject(feature);
        if (projectionPrint === 'EPSG:33952') {
          const proj4Str = this.proj4Def_.replace('lat_center', latCenter);
          geoJSONFeature.geometry.coordinates =
            this.transformCoords(proj4Str, geoJSONFeature.geometry.coordinates);
        }
      }

      geoJSONFeature.properties = {
        _gx_style: styleIds,
        name: nameFeature,
      };

      res = geoJSONFeature;
    }
    return {
      style: newStyle, geojson: res, plusIndexStyle,
    };
  }

  resizeImgData(imgData, factor) {
    const prefix = imgData.substring(0, imgData.indexOf('base64,') + 7);
    let svg = `${window.atob(imgData.replace(prefix, ''))}`;
    const xmlDoc = new DOMParser().parseFromString(svg, 'text/xml');
    let width = xmlDoc.firstChild.getAttribute('width');
    let height = xmlDoc.firstChild.getAttribute('height');
    width = Number.parseInt(width, 10);
    height = Number.parseInt(height, 10);
    xmlDoc.firstChild.setAttribute('width', `${width * factor}`);
    xmlDoc.firstChild.setAttribute('height', `${height * factor}`);
    xmlDoc.firstChild.setAttribute('viewBox', `0 0 ${width * factor} ${height * factor}`);
    const textChild = xmlDoc.firstChild.lastChild;
    let x = textChild.getAttribute('x');
    let y = textChild.getAttribute('y');
    x = Number.parseInt(x, 10) * factor;
    y = Number.parseInt(y, 10) * factor;
    let styleText = textChild.getAttribute('style');
    const styleTextArray = styleText.split(';');
    styleText = '';
    styleTextArray.forEach((s) => {
      if (s.trim().startsWith('font-size')) {
        const size = Number.parseInt(s.split(':')[1].trim().replace('px', ''), 10);
        styleText += `font-size: ${size * factor}px;`;
      } else {
        styleText += `${s};`;
      }
    });
    textChild.setAttribute('x', `${x}`);
    textChild.setAttribute('y', `${y}`);
    textChild.setAttribute('style', `${styleText}`);

    const pathChild = xmlDoc.firstChild.firstChild;
    pathChild.setAttribute('d', `m 0,${(height - 1) * factor} c ${(width - 1) * factor},0 ${width * factor},0 ${width * factor},0`);
    let pathStyle = pathChild.getAttribute('style');
    const styleArray = pathStyle.split(';');
    pathStyle = '';
    styleArray.forEach((s) => {
      if (s.trim().startsWith('stroke-dasharray')) {
        const size = Number.parseInt(s.split(':')[1].trim().replace('px', ''), 10);
        pathStyle += `stroke-dasharray: ${size * factor}px;`;
      } if (s.trim().startsWith('stroke-width')) {
        const size = Number.parseInt(s.split(':')[1].trim().replace('px', ''), 10);
        pathStyle += `stroke-width: ${size * factor}px;`;
      } else {
        pathStyle += `${s};`;
      }
    });
    pathChild.setAttribute('style', pathStyle);
    svg = new XMLSerializer().serializeToString(xmlDoc.documentElement);

    return `${prefix}${window.btoa(svg)}`;
  }

  encodeGeometryCollection_(layer, gc, style, index, indexGeom, latCenter) {
    const res = [];
    const stylesNames = {};
    let nameFeature;
    let bbox = this.facadeMap_.getBbox();
    bbox = [bbox.x.min, bbox.y.min, bbox.x.max, bbox.y.max];
    let filter;
    let newStyle = `${style}`;
    let plusIndex = 0;
    let plusIndexGeom = 0;
    gc.getGeometry().getGeometries().forEach((geometry) => {
      let parseType;
      if (geometry.getType().toLowerCase() === 'multipolygon') {
        parseType = 'polygon';
      } else if (geometry.getType().toLowerCase() === 'multipoint') {
        parseType = 'point';
      } else if (geometry.getType().toLowerCase().indexOf('linestring') > -1) {
        parseType = 'line';
      } else {
        parseType = geometry.getType().toLowerCase();
      }

      if (parseType === 'polygon') {
        const styleGeom = {
          type: parseType,
          fillColor: '#3399CC',
          fillOpacity: 0,
          strokeColor: '#3399CC',
          strokeWidth: 2,
          strokeLinecap: 'round',
        };

        nameFeature = `draw${index}`;
        if (!M.utils.isNullOrEmpty(geometry) && geometry.intersectsExtent(bbox)) {
          const styleStr = JSON.stringify(styleGeom);
          let styleName = stylesNames[styleStr];
          if (M.utils.isUndefined(styleName)) {
            const symbolizers = [];
            if (!M.utils.isNullOrEmpty(geometry) && geometry.intersectsExtent(bbox) &&
              M.utils.isUndefined(styleName)) {
              styleName = indexGeom;
              stylesNames[styleStr] = styleName;
              symbolizers.push(styleStr);
              plusIndexGeom += 1;
              plusIndex += 1;
            }

            if (styleName === undefined) {
              styleName = 0;
            }
            filter = `"[_gx_style ='${styleName}']"`;
            if (!M.utils.isNullOrEmpty(symbolizers)) {
              const a = `${filter}: {"symbolizers": [${symbolizers}]}`;
              if (newStyle !== '') {
                // eslint-disable-next-line no-param-reassign
                newStyle += `,${a}`;
              } else {
                // eslint-disable-next-line no-param-reassign
                newStyle += `{${a},"version":"2"`;
              }
            }
          }

          const geoJSONFeature = {
            type: 'Feature',
            geometry:
              this.getGeometryTransformed(geometry.getType(), geometry.getCoordinates(), latCenter),
            properties: {
              _gx_style: styleName,
              name: nameFeature,
            },
          };

          res.push(geoJSONFeature);
        }
      }
    });

    return {
      style: newStyle, geojson: res, plusIndex, plusIndexGeom,
    };
  }

  getGeometryTransformed(geomType, coordinates, latCenter) {
    const proj4Str = this.proj4Def_.replace('lat_center', latCenter);
    const newCoords = this.transformCoords(proj4Str, coordinates);
    return {
      type: geomType,
      coordinates: newCoords,
    };
  }

  transformCoords(proj4Str, coords) {
    let newCoords = [];
    if (Array.isArray(coords[0])) {
      coords.forEach(c => newCoords.push(this.transformCoords(proj4Str, c)));
    } else {
      newCoords = proj4('EPSG:3857', proj4Str, coords);
    }
    return newCoords;
  }

  getImageOffset(image, imgSize) {
    let xOffset = 0;
    let yOffset = 0;
    if (!M.utils.isNullOrEmpty(image) && image.getSrc) {
      const imageSrc = image.getSrc();
      const specialImages = ['ais.svg', 'dgps.svg', 'racon.svg', 'rc.svg'];
      // const specialOffsets = [
      //   { x: 12, y: -6 },
      //   { x: -26, y: -6 },
      //   { x: 24, y: 6 },
      //   { x: -17, y: 6 },
      // ];
      const specialIndex = specialImages.indexOf(imageSrc.substring(imageSrc.lastIndexOf('/') + 1));
      if (specialIndex > -1) {
        // return specialOffsets[specialIndex];
        return { x: -11, y: 0 };
      } else if (imageSrc.startsWith('data:image/svg')) {
        const txtNie = Buffer.from(imageSrc.split(',')[1], 'base64').toString();
        if (txtNie.indexOf('AIS') > -1) {
          return { x: -10, y: 50 };
        } else if (txtNie.indexOf('RACON') > -1) {
          return { x: 0, y: 60 };
        } else if (txtNie.indexOf('RC') > -1) {
          return { x: -10, y: 50 };
        } else if (txtNie.indexOf('DGPS') > -1) {
          return { x: -10, y: 50 };
        }
        // return { x: 30, y: 30 };
        return { x: 80, y: 80 };
      }
      const anchorProp = 'anchor_';
      const xUnitsProp = 'anchorXUnits_';
      const yUnitsProp = 'anchorYUnits_';
      const anchor = image[anchorProp];
      const xUnits = image[xUnitsProp];
      const yUnits = image[yUnitsProp];

      let anchorX = anchor[0];
      let anchorY = anchor[1];
      if (xUnits === 'fraction') {
        anchorX *= imgSize[0];
      }
      if (yUnits === 'fraction') {
        anchorY *= imgSize[1];
      }
      xOffset = (imgSize[0] / 2) - anchorX;
      yOffset = (imgSize[1] / 2) - anchorY;
    }

    return { x: xOffset, y: yOffset };
  }

  encodeWMTSToWMS(layer, center) {
    const layerName = layer.url.indexOf('enccache') > 0 ? `${layer.name}_7cs-wms` : `${layer.name}-wms`;

    const encodedLayer = {
      baseURL: this.mapProxyUrl_,
      opacity: 1,
      type: 'WMS',
      layers: [layerName],
      customParams: {
        transparent: true,
        center_epsg: center,
      },
    };

    return encodedLayer;
  }

  /**
   * This function
   *
   * @public
   * @function
   * @param {M.Map} map to add the plugin
   * @param {function} template template of this control
   * @api stable
   */
  encodeWMTS(layer) {
    const layerImpl = layer.getImpl();
    const olLayer = layerImpl.getOL3Layer();
    const layerSource = olLayer.getSource();

    const layerUrl = layer.url;
    const layerName = layer.name;
    const layerOpacity = olLayer.getOpacity();
    const layerReqEncoding = layerSource.getRequestEncoding();
    const matrixSet = layer.matrixSet;

    /**
     * @see http: //www.mapfish.org/doc/print/protocol.html#layers-params
     */
    return layer.getImpl().getCapabilities().then((capabilities) => {
      const matrixIdsObj = capabilities.Contents.TileMatrixSet.filter((tileMatrixSet) => {
        return (tileMatrixSet.Identifier === matrixSet || tileMatrixSet.Identifier === matrixSet.replace('EPSG:', ''));
      })[0];

      try {
        return {
          baseURL: layerUrl,
          imageFormat: layer.options.imageFormat || layer.options.format || 'image/png',
          layer: layerName,
          matrices: matrixIdsObj.TileMatrix.map((tileMatrix, i) => {
            return {
              identifier: tileMatrix.Identifier,
              matrixSize: [tileMatrix.MatrixHeight, tileMatrix.MatrixWidth],
              scaleDenominator: tileMatrix.ScaleDenominator,
              tileSize: [tileMatrix.TileWidth, tileMatrix.TileHeight],
              topLeftCorner: tileMatrix.TopLeftCorner,
            };
          }),
          matrixSet: matrixIdsObj.Identifier,
          opacity: layerOpacity,
          requestEncoding: layerReqEncoding,
          style: 'default',
          type: 'WMTS',
          version: '1.3.0',
        };
      } catch (e) {
        M.dialog.error(getValue('errorProjectionCapabilities'));
        return null;
      }
    });
  }

  getCenterByBbox(bbox) {
    const center = [(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2];
    return ol.proj.transform(center, this.facadeMap_.getProjection().code, 'EPSG:4326');
  }

  /**
   * This function reprojects map on selected SRS.
   *
   * @function
   * @param {string} origin - EPSG:25830
   * @param {array<number>} coordinates pair
   * @api
   */
  reproject(origin, coordinates) {
    const originProj = ol.proj.get(origin);
    const destProj = ol.proj.get('EPSG:4326');
    const coordinatesTransform = ol.proj.transform(coordinates, originProj, destProj);
    return coordinatesTransform;
  }

  transformExt(box, code, currProj) {
    return ol.proj.transformExtent(box, code, currProj);
  }

  /**
   * This function destroys this control, clearing the HTML
   * and unregistering all events
   *
   * @public
   * @function
   * @api stable
   */
  destroy() {
    this.facadeMap_.getMapImpl().removeControl(this);
    this.facadeMap_ = null;
  }

  inflateCoordinates(flatCoordinates, offset, end, stride, optCoordinates) {
    const coordinates = optCoordinates !== undefined ? optCoordinates : [];
    let i = 0;
    for (let j = offset; j < end[0]; j += stride) {
      // eslint-disable-next-line no-plusplus
      coordinates[i++] = flatCoordinates.slice(j, j + stride);
    }

    coordinates.length = i;
    return coordinates;
  }

  inflateCoordinatesArray(flatCoordinates, offset, ends, stride, optCoordinatess) {
    const coordinatess = optCoordinatess !== undefined ? optCoordinatess : [];
    let i = 0;
    // eslint-disable-next-line no-plusplus
    for (let j = 0, jj = ends.length; j < jj; ++j) {
      const end = ends[j];
      // eslint-disable-next-line no-plusplus
      coordinatess[i++] = this.inflateCoordinates(
        flatCoordinates,
        offset,
        end,
        stride,
        coordinatess[i],
      );
      // eslint-disable-next-line no-param-reassign
      offset = end;
    }

    coordinatess.length = i;
    return coordinatess;
  }

  /**
   * Activates edition mode.
   * @public
   * @function
   * @api
   */
  activateEdition() {
    const olMap = this.facadeMap_.getMapImpl();
    const extentLayer = this.facadeControl.extentLayer.getImpl().getOL3Layer();

    if (extentLayer) {
      this.edit = new ol.interaction.Translate({ layers: [extentLayer] });
      olMap.addInteraction(this.edit);
    }
  }

  /**
   * Removes edit interaction
   * @public
   * @api
   * @function
   */
  removeEditInteraction() {
    this.facadeMap_.getMapImpl().removeInteraction(this.edit);
  }

  /**
   * This function adds figure interaction to map.
   * @public
   * @function
   * @api
   */
  addFigureInteraction() {
    this.vectorSource = this.newVectorSource(false);
    const olMap = this.facadeMap_.getMapImpl();
    this.drawRegular = this.newDrawFigureInteraction(this.vectorSource);
    this.addDrawFigureEvent();
    olMap.addInteraction(this.drawRegular);
  }

  /**
   * Defines function to be executed on click on figure interaction.
   * @public
   * @function
   * @api
   */
  addDrawFigureEvent() {
    this.drawRegular.on('drawend', (event) => {
      this.facadeControl.onDrawFigure(event);
    });
  }

  /**
   * Creates new OpenLayers draw regular interaction
   * @public
   * @function
   * @api
   * @param {OLVectorSource} vectorSource -
   */
  newDrawFigureInteraction(vectorSource) {
    const active = () => {
      return false;
    };
    return new DrawRegular({
      canRotate: false,
      sides: 4,
      source: vectorSource,
      squareCondition: active,
    });
  }

  /**
   * Removes draw regular interaction from map.
   * @public
   * @function
   * @api
   */
  removeFigureDrawInteraction() {
    this.facadeMap_.getMapImpl().removeInteraction(this.drawRegular);
  }
}
